/**
 * BillingPlanRepository.ts
 * 빌링 플랜 관리 Repository (Admin)
 */

import { API_BASE_URL } from '../../../config/api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface BillingPlan {
  id: string;
  name: string;
  description?: string;
  monthly_base_fee: number;
  alimtalk_unit_price: number;
  alimtalk_free_quota: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePlanRequest {
  name: string;
  description?: string;
  monthly_base_fee?: number;
  alimtalk_unit_price?: number;
  alimtalk_free_quota?: number;
}

export interface UpdatePlanRequest {
  name?: string;
  description?: string;
  monthly_base_fee?: number;
  alimtalk_unit_price?: number;
  alimtalk_free_quota?: number;
  is_active?: boolean;
}

export interface OrganizationSubscription {
  id: string;
  organization_id: string;
  plan_id?: string;
  custom_monthly_base_fee?: number;
  custom_alimtalk_unit_price?: number;
  custom_alimtalk_free_quota?: number;
  start_date: string;
  end_date?: string;
  status: 'active' | 'suspended' | 'cancelled';
  notes?: string;
  billing_plans?: BillingPlan;
  organizations?: {
    id: string;
    name: string;
  };
}

export interface AssignSubscriptionRequest {
  plan_id?: string;
  custom_monthly_base_fee?: number;
  custom_alimtalk_unit_price?: number;
  custom_alimtalk_free_quota?: number;
  notes?: string;
}

export interface MonthlyCharge {
  id: string;
  organization_id: string;
  year_month: string;
  charge_type: string;
  description?: string;
  amount: number;
  created_by?: string;
  created_at: string;
}

export interface AddChargeRequest {
  charge_type: string;
  description?: string;
  amount: number;
}

export interface MonthlyBill {
  year_month: string;
  plan_name: string;
  breakdown: {
    monthly_base_fee: number;
    alimtalk: {
      total_sent: number;
      free_quota: number;
      billable_count: number;
      unit_price: number;
      cost: number;
    };
    additional_charges: number;
    charge_details: MonthlyCharge[];
  };
  total: number;
}

// ============================================================================
// Repository Class
// ============================================================================

export class BillingPlanRepository {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private async apiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ================================================================
  // Plan CRUD
  // ================================================================

  async getAllPlans(includeInactive = false): Promise<BillingPlan[]> {
    const result = await this.apiCall<{ plans: BillingPlan[] }>(
      `/api/v1/admin/billing/plans?include_inactive=${includeInactive}`
    );
    return result.plans;
  }

  async getPlan(planId: string): Promise<BillingPlan> {
    const result = await this.apiCall<{ plan: BillingPlan }>(
      `/api/v1/admin/billing/plans/${planId}`
    );
    return result.plan;
  }

  async createPlan(data: CreatePlanRequest): Promise<BillingPlan> {
    const result = await this.apiCall<{ plan: BillingPlan }>(
      `/api/v1/admin/billing/plans`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return result.plan;
  }

  async updatePlan(planId: string, data: UpdatePlanRequest): Promise<BillingPlan> {
    const result = await this.apiCall<{ plan: BillingPlan }>(
      `/api/v1/admin/billing/plans/${planId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return result.plan;
  }

  async deletePlan(planId: string): Promise<void> {
    await this.apiCall<{ success: boolean }>(
      `/api/v1/admin/billing/plans/${planId}`,
      { method: 'DELETE' }
    );
  }

  // ================================================================
  // Organization Subscriptions
  // ================================================================

  async getAllSubscriptions(): Promise<OrganizationSubscription[]> {
    const result = await this.apiCall<{ subscriptions: OrganizationSubscription[] }>(
      `/api/v1/admin/billing/subscriptions`
    );
    return result.subscriptions;
  }

  async getOrganizationSubscription(organizationId: string): Promise<OrganizationSubscription | null> {
    const result = await this.apiCall<{
      subscription: OrganizationSubscription | null;
      has_subscription: boolean;
    }>(`/api/v1/admin/billing/organizations/${organizationId}/subscription`);
    return result.subscription;
  }

  async assignSubscription(
    organizationId: string,
    data: AssignSubscriptionRequest
  ): Promise<OrganizationSubscription> {
    const result = await this.apiCall<{ subscription: OrganizationSubscription }>(
      `/api/v1/admin/billing/organizations/${organizationId}/subscription`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return result.subscription;
  }

  // ================================================================
  // Monthly Charges
  // ================================================================

  async getOrganizationCharges(
    organizationId: string,
    yearMonth?: string
  ): Promise<MonthlyCharge[]> {
    const url = yearMonth
      ? `/api/v1/admin/billing/organizations/${organizationId}/charges?year_month=${yearMonth}`
      : `/api/v1/admin/billing/organizations/${organizationId}/charges`;

    const result = await this.apiCall<{ charges: MonthlyCharge[] }>(url);
    return result.charges;
  }

  async addCharge(
    organizationId: string,
    yearMonth: string,
    data: AddChargeRequest
  ): Promise<MonthlyCharge> {
    const result = await this.apiCall<{ charge: MonthlyCharge }>(
      `/api/v1/admin/billing/organizations/${organizationId}/charges/${yearMonth}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return result.charge;
  }

  async deleteCharge(chargeId: string): Promise<void> {
    await this.apiCall<{ success: boolean }>(
      `/api/v1/admin/billing/charges/${chargeId}`,
      { method: 'DELETE' }
    );
  }

  // ================================================================
  // Billing Calculation
  // ================================================================

  async getOrganizationBill(
    organizationId: string,
    yearMonth: string
  ): Promise<MonthlyBill> {
    const result = await this.apiCall<{ bill: MonthlyBill }>(
      `/api/v1/admin/billing/organizations/${organizationId}/bill/${yearMonth}`
    );
    return result.bill;
  }
}

export const billingPlanRepository = new BillingPlanRepository();
